import React, { useState, useEffect } from 'react';
import AdminLogin from '../components/admin/AdminLogin';
import AdminHeader from '../components/admin/AdminHeader';
import UploadSection from '../components/admin/UploadSection';
import EventFilter from '../components/admin/EventFilter';
import ResponseFilter, { ResponseFilterType } from '../components/admin/ResponseFilter';
import SearchBar from '../components/admin/SearchBar';
import StatisticsCards from '../components/admin/StatisticsCards';
import GuestTable from '../components/admin/GuestTable';
import GuestModal from '../components/admin/GuestModal';
import SeatingPlan from '../components/admin/SeatingPlan';
import MessagesPanel from '../components/admin/MessagesPanel';

interface Event {
  id: number;
  name: string;
  description?: string;
  order: number;
}

interface GuestWithInvitations {
  id: number;
  firstName: string;
  lastName?: string;
  phoneNumber?: string;
  message?: string;
  messageForCaterer: boolean;
  invitations: Array<{
    eventId: number;
    event: {
      id: number;
      name: string;
    };
  }>;
  rsvps: Array<{
    eventId: number;
    event: {
      id: number;
      name: string;
    };
    attending: boolean;
  }>;
}

const Admin: React.FC = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [guests, setGuests] = useState<GuestWithInvitations[]>([]);
  const [events, setEvents] = useState<Event[]>([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState<any>(null);

  // Active tab
  const [activeTab, setActiveTab] = useState<'guests' | 'messages' | 'seating'>('guests');

  // Selected event filter ('all' or event ID)
  const [selectedFilter, setSelectedFilter] = useState<'all' | number>('all');

  // Selected response filter
  const [selectedResponseFilter, setSelectedResponseFilter] = useState<ResponseFilterType>('all');

  // Search query
  const [searchQuery, setSearchQuery] = useState<string>('');

  // Modal state
  const [selectedGuestForModal, setSelectedGuestForModal] = useState<GuestWithInvitations | null>(
    null
  );
  const [isAddGuestModalOpen, setIsAddGuestModalOpen] = useState(false);

  useEffect(() => {
    checkAuth();
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      loadGuests();
      loadEvents();
    }
  }, [isAuthenticated]);

  const checkAuth = async () => {
    try {
      const response = await fetch('/api/admin/check');
      const data = await response.json();
      setIsAuthenticated(data.isAdmin);
    } catch (err) {
      console.error('Error checking auth:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async (password: string) => {
    setError('');

    try {
      const response = await fetch('/api/admin/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ password }),
      });

      const data = await response.json();

      if (data.success) {
        setIsAuthenticated(true);
      } else {
        setError('Mot de passe incorrect');
      }
    } catch (err) {
      setError('Erreur de connexion');
    }
  };

  const handleLogout = async () => {
    try {
      await fetch('/api/admin/logout', { method: 'POST' });
      setIsAuthenticated(false);
      setGuests([]);
    } catch (err) {
      console.error('Error logging out:', err);
    }
  };

  const loadGuests = async () => {
    try {
      const response = await fetch('/api/admin/guests');
      if (response.ok) {
        const data = await response.json();
        setGuests(data);
      }
    } catch (err) {
      console.error('Error loading guests:', err);
    }
  };

  const loadEvents = async () => {
    try {
      const response = await fetch('/api/events');
      if (response.ok) {
        const data = await response.json();
        setEvents(data);
      }
    } catch (err) {
      console.error('Error loading events:', err);
    }
  };

  const handleDelete = async (id: number) => {
    if (selectedFilter === 'all') {
      // Supprimer complètement l'invité
      if (!window.confirm('Êtes-vous sûr de vouloir supprimer cet invité ?')) {
        return;
      }

      try {
        const response = await fetch(`/api/admin/guests/${id}`, {
          method: 'DELETE',
        });

        if (response.ok) {
          setGuests(guests.filter((g) => g.id !== id));
        }
      } catch (err) {
        console.error('Error deleting guest:', err);
      }
    } else {
      // Désinviter de l'événement spécifique
      if (!window.confirm('Êtes-vous sûr de vouloir désinviter cette personne de cet événement ?')) {
        return;
      }

      try {
        const response = await fetch(`/api/admin/guests/${id}/events/${selectedFilter}`, {
          method: 'DELETE',
        });

        if (response.ok) {
          loadGuests();
        }
      } catch (err) {
        console.error('Error removing invitation:', err);
      }
    }
  };

  const handleSaveGuest = async (id: number | null, firstName: string, lastName: string, phoneNumber: string, eventIds: number[]): Promise<number> => {
    try {
      let guestId = id;

      if (id === null) {
        // Add new guest
        const response = await fetch('/api/admin/guests', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ firstName, lastName, phoneNumber, eventIds }),
        });

        if (!response.ok) {
          const data = await response.json();
          throw new Error(data.error || 'Erreur lors de l\'ajout');
        }

        const data = await response.json();
        guestId = data.guest.id;
      } else {
        // Update existing guest
        const response = await fetch(`/api/admin/guests/${id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ firstName, lastName, phoneNumber, eventIds }),
        });

        if (!response.ok) {
          const data = await response.json();
          throw new Error(data.error || 'Erreur lors de la mise à jour');
        }
      }

      loadGuests();
      return guestId!;
    } catch (err: any) {
      throw err;
    }
  };

  const handleUpload = async () => {
    if (!uploadFile) return;

    setUploading(true);
    setUploadResult(null);

    try {
      const formData = new FormData();
      formData.append('file', uploadFile);

      const response = await fetch('/api/admin/import', {
        method: 'POST',
        body: formData,
      });

      const data = await response.json();
      setUploadResult(data);

      if (data.success) {
        loadGuests();
        setUploadFile(null);
      }
    } catch (err) {
      setUploadResult({ success: false, error: "Erreur lors de l'import" });
    } finally {
      setUploading(false);
    }
  };

  const updateRsvp = async (
    guestId: number,
    eventId: number,
    attending: boolean | null
  ) => {
    try {
      const response = await fetch(`/api/admin/guests/rsvp/${guestId}/${eventId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ attending }),
      });

      if (response.ok) {
        loadGuests();
      }
    } catch (err) {
      console.error('Error updating RSVP:', err);
    }
  };

  const toggleMessageCaterer = async (guestId: number, value: boolean) => {
    try {
      const response = await fetch(`/api/admin/guests/${guestId}/message-caterer`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ messageForCaterer: value }),
      });

      if (response.ok) {
        setGuests(guests.map(g => g.id === guestId ? { ...g, messageForCaterer: value } : g));
      }
    } catch (err) {
      console.error('Error toggling caterer flag:', err);
    }
  };

  const isInvitedToEvent = (guest: GuestWithInvitations, eventId: number): boolean => {
    return guest.invitations.some((inv) => inv.event.id === eventId);
  };

  // Get stats for selected filter
  const getStats = () => {
    if (selectedFilter === 'all') {
      return {
        allStats: {
          totalGuests: guests.length,
          totalResponded: guests.filter((g) => g.rsvps.length > 0).length,
          totalAttending: guests.reduce(
            (sum, g) => sum + (g.rsvps.filter((r) => r.attending).length > 0 ? 1 : 0),
            0
          ),
        },
        eventStats: undefined,
      };
    } else {
      const invitedGuests = guests.filter((g) => isInvitedToEvent(g, selectedFilter));
      const responded = invitedGuests.filter((g) =>
        g.rsvps.some((r) => r.eventId === selectedFilter)
      );
      const yesResponses = invitedGuests.filter((g) => {
        const rsvp = g.rsvps.find((r) => r.eventId === selectedFilter);
        return rsvp?.attending === true;
      });
      const noResponses = invitedGuests.filter((g) => {
        const rsvp = g.rsvps.find((r) => r.eventId === selectedFilter);
        return rsvp?.attending === false;
      });

      return {
        allStats: undefined,
        eventStats: {
          totalInvited: invitedGuests.length,
          totalYes: yesResponses.length,
          totalNo: noResponses.length,
          totalPending: invitedGuests.length - responded.length,
        },
      };
    }
  };

  const { allStats, eventStats } = getStats();

  // Filter events to display
  const eventsToDisplay =
    selectedFilter === 'all' ? events : events.filter((e) => e.id === selectedFilter);

  // Helper function to check if guest matches response filter
  const matchesResponseFilter = (guest: GuestWithInvitations): boolean => {
    if (selectedResponseFilter === 'all') return true;

    const relevantRsvps = selectedFilter === 'all'
      ? guest.rsvps
      : guest.rsvps.filter(r => r.eventId === selectedFilter);

    if (selectedResponseFilter === 'responded') {
      return relevantRsvps.length > 0;
    }

    if (selectedResponseFilter === 'pending') {
      if (selectedFilter === 'all') {
        return guest.rsvps.length === 0;
      } else {
        return !guest.rsvps.some(r => r.eventId === selectedFilter);
      }
    }

    if (selectedResponseFilter === 'confirmed') {
      return relevantRsvps.some(r => r.attending === true);
    }

    if (selectedResponseFilter === 'declined') {
      return relevantRsvps.some(r => r.attending === false);
    }

    return true;
  };

  // Filter guests to display
  let guestsToDisplay = selectedFilter === 'all'
    ? guests
    : guests.filter((g) => isInvitedToEvent(g, selectedFilter));

  // Apply response filter
  guestsToDisplay = guestsToDisplay.filter(matchesResponseFilter);

  // Apply search filter
  if (searchQuery.trim()) {
    const query = searchQuery.toLowerCase().trim();
    guestsToDisplay = guestsToDisplay.filter((guest) => {
      const fullName = `${guest.firstName} ${guest.lastName || ''}`.toLowerCase();
      return fullName.includes(query);
    });
  }

  if (loading) {
    return <div className="admin-container">Chargement...</div>;
  }

  if (!isAuthenticated) {
    return <AdminLogin onLogin={handleLogin} error={error} />;
  }

  return (
    <div className="modern-admin">
      <AdminHeader
        totalGuests={guests.length}
        filteredGuests={guestsToDisplay.length}
        isFiltered={selectedFilter !== 'all'}
        onLogout={handleLogout}
      />

      <div className="admin-tabs">
        <button
          className={`tab-button ${activeTab === 'guests' ? 'active' : ''}`}
          onClick={() => setActiveTab('guests')}
        >
          Gestion des invités
        </button>
        <button
          className={`tab-button ${activeTab === 'messages' ? 'active' : ''}`}
          onClick={() => setActiveTab('messages')}
        >
          Messages
        </button>
        <button
          className={`tab-button ${activeTab === 'seating' ? 'active' : ''}`}
          onClick={() => setActiveTab('seating')}
        >
          Plan de table
        </button>
      </div>

      <div className="admin-content">
        {activeTab === 'guests' ? (
          <>
        <UploadSection
          uploadFile={uploadFile}
          uploading={uploading}
          uploadResult={uploadResult}
          onFileChange={setUploadFile}
          onUpload={handleUpload}
        />

        <EventFilter
          events={events}
          selectedFilter={selectedFilter}
          onFilterChange={setSelectedFilter}
        />

        <StatisticsCards
          isFiltered={selectedFilter !== 'all'}
          allStats={allStats}
          eventStats={eventStats}
        />

        <ResponseFilter
          selectedFilter={selectedResponseFilter}
          onFilterChange={setSelectedResponseFilter}
        />

        <div className="admin-actions-row">
          <SearchBar
            searchQuery={searchQuery}
            onSearchChange={setSearchQuery}
          />
          <button
            className="modern-btn primary add-guest-btn"
            onClick={() => setIsAddGuestModalOpen(true)}
          >
            + Ajouter un invité
          </button>
        </div>

        <GuestTable
          guests={guestsToDisplay}
          events={eventsToDisplay}
          onGuestClick={setSelectedGuestForModal}
          onDelete={handleDelete}
          onUpdateRsvp={updateRsvp}
        />
          </>
        ) : activeTab === 'messages' ? (
          <MessagesPanel
            guests={guests}
            onToggleCaterer={toggleMessageCaterer}
          />
        ) : (
          <SeatingPlan />
        )}
      </div>

      <GuestModal
        guest={selectedGuestForModal}
        isOpen={isAddGuestModalOpen}
        events={events}
        onClose={() => {
          setSelectedGuestForModal(null);
          setIsAddGuestModalOpen(false);
        }}
        onSave={handleSaveGuest}
      />
    </div>
  );
};

export default Admin;
